import { IBoundingBox, IPolygon } from "@backend/src/schemas";
import {
  getGlobalPolygon,
  getLocalPolygon,
  getPolygonCoordinateSystem,
} from "../../snapToGrid/snapToGrid";

interface IArgs {
  spacePolygon: IPolygon;
  fireDeviceCoordinates: [number, number][];
  squareSize: {
    width: number;
    height: number;
  };
}

export interface IFireDeviceSquareBoxesWithCoversValidation {
  squares: IBoundingBox[];
  wallSegments: [[number, number], [number, number]][];
  coverAllWalls: boolean;
}

interface IWallCoverage {
  wallSegments: [[number, number], [number, number]][];
  sizes: [number, number][];
  coverAllWalls: boolean;
  exposedLength: number;
}
const segmentLength = (wallSegment: [[number, number], [number, number]]): number => {
  return Math.sqrt(
    (wallSegment[1][0] - wallSegment[0][0]) ** 2 + (wallSegment[1][1] - wallSegment[0][1]) ** 2
  );
};

export const getOverlap = (
  segm: [[number, number], [number, number]],
  device: [number, number],
  size: [number, number]
): [[number, number], [number, number]][] => {
  const px = segm[0][0];
  const py = segm[0][1];
  const qx = segm[1][0];
  const qy = segm[1][1];
  const sx = size[0];
  const sy = size[1];
  // line(k) = p + a(q - p)

  let x0 = Infinity;
  let x1 = -Infinity;
  let y0 = Infinity;
  let y1 = -Infinity;
  if (Math.abs(qx - px) > 0) {
    const a = (device[0] - sx - px) / (qx - px);
    const b = (device[0] + sx - px) / (qx - px);
    x0 = Math.min(a, b);
    x1 = Math.max(a, b);
  } else if (device[0] - sx <= px && px <= device[0] + sx) {
    x0 = -Infinity;
    x1 = Infinity;
  }
  if (Math.abs(qy - py) > 0) {
    const c = (device[1] - sy - py) / (qy - py);
    const d = (device[1] + sy - py) / (qy - py);
    y0 = Math.min(c, d);
    y1 = Math.max(c, d);
  } else if (device[1] - sy <= py && py <= device[1] + sy) {
    y0 = -Infinity;
    y1 = Infinity;
  }
  const a0 = Math.max(x0, y0);
  const a1 = Math.min(x1, y1);
  if (a0 <= 0 && a1 >= 1) {
    // Completely covered, other result obsolete
    return [];
  }
  if (a0 > a1) {
    // Line outside of rect, this result obsolete
    return [segm];
  }
  let newLines = [] as [[number, number], [number, number]][];
  if (0 < a0) {
    // Beginning of line exposed
    newLines.push([
      [px, py],
      [px + Math.min(a0, 1) * (qx - px), py + Math.min(a0, 1) * (qy - py)],
    ]);
    // compare beginning line end deem which is obsolete
  }
  if (a1 < 1) {
    // End of line exposed
    newLines.push([
      [px + Math.max(a1, 0) * (qx - px), py + Math.max(a1, 0) * (qy - py)],
      [qx, qy],
    ]);
    // compare end line start to deem which is obsolete
  }
  // Speed up strategy: compare results to identify if one or both orientation is obsolete for this segment
  return newLines;
};

export const recursiveFireDeviceFunction = (
  deviceCoordinates: [number, number][],
  deviceIndex: number,
  listOfWallSegments: [[number, number], [number, number]][],
  size: [number, number]
): IWallCoverage[] => {
  if (deviceIndex >= deviceCoordinates.length) {
    let totLen = 0;
    for (var segm of listOfWallSegments) {
      totLen += segmentLength(segm);
    }
    return [
      {
        wallSegments: listOfWallSegments,
        sizes: [],
        coverAllWalls: totLen === 0,
        exposedLength: totLen,
      },
    ];
  }
  const device = deviceCoordinates[deviceIndex] as [number, number];
  let results = [];
  for (var tempSize of [size, [size[1], size[0]]]) {
    let newSegments = [];

    for (var segm of listOfWallSegments) {
      const newOverlap = getOverlap(segm, device, tempSize as [number, number]);
      newSegments.push(...newOverlap);
    }
    let tempResults = recursiveFireDeviceFunction(
      deviceCoordinates,
      deviceIndex + 1,
      newSegments,
      size
    );
    for (var r of tempResults) {
      r.sizes.unshift(tempSize as [number, number]);
    }
    results.push(...tempResults);
  }
  return results;
  /*if (results[1].exposedLength < results[0].exposedLength) {
    results[1].sizes.unshift([size[1], size[0]] as [number, number]);

    return results[1];
  } else {
    results[0].sizes.unshift(size);
    return results[0];
  }*/
};

const getDeviceBoundingBox = (c: [number, number], s: [number, number]): [number, number][] => {
  const outp = [
    [c[0] - s[0], c[1] - s[1]],
    [c[0] + s[0], c[1] - s[1]],
    [c[0] + s[0], c[1] + s[1]],
    [c[0] - s[0], c[1] + s[1]],
    [c[0] - s[0], c[1] - s[1]],
  ];
  return outp as [number, number][];
};

export const getFireDeviceSquareBoxesAllCombinationsWithValidations = (
  args: IArgs
): IFireDeviceSquareBoxesWithCoversValidation[] => {
  //TODO: Implement
  // Convert to local coordinates so that size aligns with x-y.
  const polyBox = getPolygonCoordinateSystem(args.spacePolygon);
  const localPolygon = getLocalPolygon(args.spacePolygon, polyBox) as [number, number][];
  const localDevices = getLocalPolygon(args.fireDeviceCoordinates, polyBox) as [number, number][];
  const localSegments = localPolygon.map((vert, i, pol) => [vert, pol[(i + 1) % pol.length]]) as [
    [number, number],
    [number, number],
  ][];
  const allResults = recursiveFireDeviceFunction(
    localDevices as [number, number][],
    0,
    localSegments,
    [args.squareSize.width, args.squareSize.height]
  );
  let output = [];
  for (var result of allResults) {
    const localSquares = localDevices.map((coord, index) =>
      getDeviceBoundingBox(coord as [number, number], result.sizes[index])
    );
    const globalSquares = localSquares.map((localSquare) => getGlobalPolygon(localSquare, polyBox));
    output.push({
      squares: globalSquares as IBoundingBox[],
      wallSegments: result.wallSegments,
      coverAllWalls: result.coverAllWalls,
    });
  }
  return output;
};
